import React, { useContext, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFonts } from '@use-expo/font';
import MyMessagePopup from '../components/MyMessagePopup';
import MyEditUserPopup from '../components/MyEditUserPopup';
import MyAskQuestionPopup from '../components/MyAskQuestionPopup';
import MyTableHeader from '../components/MyTableHeader';
import MyTableLine from '../components/MyTableLine';
import { calcInitials, LocStr } from '../utils/Utils';
import { Context as PayKeyDashboardServerContext } from '../context/PayKeyDashboardServerContext';
import { expo } from '../../app.json';
import { img_paykey } from '../utils/ImagesManagementUtils';
import {
  Add_user,
  can_not_block,
  can_not_delete_yourself,
  Customers_str,
  log_out,
  no_str,
  ok_str,
  PayKey_team,
  Sorry_window_size,
  want_delete_user,
  want_to_block,
  want_to_exit,
  want_to_unblock,
  yes_str,
} from '../utils/DictionaryManagementUtils';
import {
  font_Quicksand_Bold,
  font_Quicksand_Light,
  font_Quicksand_Medium,
  font_Quicksand_Regular,
  font_Quicksand_SemiBold,
  font_rubik_Bold,
  font_rubik_Medium,
  font_rubik_Regular,
} from '../utils/FontsManagementUtils';

//----------------------------------------------------------------------

const window = Dimensions.get('window');
const screen = Dimensions.get('screen');

export default function AdminScreen({ navigation }) {
  // ============= Incoming params ====================

  const { token } = navigation.state.params;
  const { loggedInUserId } = navigation.state.params;

  const { userItem } = navigation.state.params;

  const { language } = navigation.state.params;

  // ============= variables ====================

  const [state, setState] = useState({
    admins: [],
    users: [],
  });

  const [animating, setAnimating] = useState(false);

  const [messagePopupState, setMessagePopupState] = useState({
    isMessageVisible: false,
    title: '',
  });

  const [questionPopupState, setQuestionPopupState] = useState({
    isQuestionVisible: false,
    questionId: '',
    userId: '',
    title: '',
  });

  const [editUserState, setEditUserState] = useState({
    isVisible: false,
    isNew: true,
    editorIsAdmin: false,
    userInfo: {},
  });

  const [mainMenuState, setMainMenuState] = useState({ isMenuOpen: false });

  //= ====================================================================

  const {
    getAllUsers,
    createUser,
    blockUser,
    unblockUser,
    deleteUser,
    updateUserByAdmin,
    getAllFiles,
    getFontFile,
  } = useContext(PayKeyDashboardServerContext);

  // ######################################################################

  const [dimensions, setDimensions] = useState({ window, screen });

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;

  useEffect(() => {
    serverLoadAllUsers();

    Dimensions.addEventListener('change', onChange);
    return () => {
      Dimensions.removeEventListener('change', onChange);
    };
  }, []);

  const onChange = ({ window, screen }) => {
    setDimensions({ window, screen });
  };

  //----------------------------------------------------------------------

  const onRefreshScreen = () => {
    setState({ ...state });
  };

  //----------------------------------------------------------------------
  const [fontsLoaded] = useFonts({
    'Rubik-Regular': font_rubik_Regular,
    'Rubik-Medium': font_rubik_Medium,
    'Rubik-Bold': font_rubik_Bold,
    'Quicksand-Bold': font_Quicksand_Bold,
    'Quicksand-Light': font_Quicksand_Light,
    'Quicksand-Medium': font_Quicksand_Medium,
    'Quicksand-Regular': font_Quicksand_Regular,
    'Quicksand-SemiBold': font_Quicksand_SemiBold,
  });

  //---------------------------------------------------------------------

  const onUserChanged = (userInfo, isNew) => {
    setEditUserState({
      isVisible: false,
      isNew: true,
      editorIsAdmin: false,
      userInfo: {},
    });

    if (isNew) {
      serverCreateUser(userInfo);
    } else {
      serverUpdateUserByAdmin(userInfo.userId, userInfo);
    }
  };

  const onUserEditCanceled = () => {
    setEditUserState({
      isVisible: false,
      isNew: true,
      editorIsAdmin: false,
      userInfo: {},
    });
  };

  //---------------------------------------------------------------------

  const onLogoutPressed = () => {
    const txt = LocStr(want_to_exit, language);
    setQuestionPopupState({
      isQuestionVisible: true,
      questionId: 'logout',
      title: txt,
    });
  };

  //---------------------------------------------------------------------

  const onQuestionAnswerWasYes = (questionId, userId) => {
    setQuestionPopupState({
      isQuestionVisible: false,
      questionId: '',
      userId: '',
      title: '',
    });

    switch (questionId) {
      case 'logout':
        navigation.pop();
        break;

      case 'delete':
        serverDeleteUser(userId);
        break;
      case 'block':
        serverBlockUser(userId);
        break;
      case 'unblock':
        serverUnblockUser(userId);
        break;
    }
  };

  const onQuestionAnswerWasNo = (questionId, userId) => {
    setQuestionPopupState({
      isQuestionVisible: false,
      questionId: '',
      userId: '',
      title: '',
    });
  };

  //---------------------------------------------------------------------

  const onActionPressed = (action, rowIndex, fromAdminsList) => {
    const selectedUser = fromAdminsList
      ? state.admins[rowIndex]
      : state.users[rowIndex];
    let txt;

    switch (action) {
      case 'edit':
        onEditUserPressed(selectedUser);
        break;

      case 'block':
        if (loggedInUserId === selectedUser.userId) {
          txt = LocStr(can_not_block, language);
          setMessagePopupState({ isMessageVisible: true, title: txt });
          return;
        }
        if (calcStatus(selectedUser) === 'Blocked') {
          txt = LocStr(want_to_unblock, language);
          setQuestionPopupState({
            isQuestionVisible: true,
            questionId: 'unblock',
            userId: selectedUser.userId,
            title: txt,
          });
        } else {
          txt = LocStr(want_to_block, language);
          setQuestionPopupState({
            isQuestionVisible: true,
            questionId: 'block',
            userId: selectedUser.userId,
            title: txt,
          });
        }
        break;

      case 'delete':
        if (loggedInUserId === selectedUser.userId) {
          txt = LocStr(can_not_delete_yourself, language);
          setMessagePopupState({ isMessageVisible: true, title: txt });
          return;
        }
        txt = LocStr(want_delete_user, language);
        setQuestionPopupState({
          isQuestionVisible: true,
          questionId: 'delete',
          userId: selectedUser.userId,
          title: txt,
        });
        break;

      case 'login':
        gotoMain(selectedUser);
        break;
    }
  };

  //---------------------------------------------------------------------

  const onAddUserPressed = () => {
    setEditUserState({
      isVisible: true,
      isNew: true,
      editorIsAdmin: userItem.userType === 'admin',
      userInfo: {
        userFullname: '',
        userInstitute: '',
        userRole: '',
        userEmail: '',
        userPhone: '',

        userUsername: '',
        userPassword: '',
        userType: '',
        userId: '',
      },
    });
  };

  const validateStr = (src) => {
    if (src === null) return '';
    return src;
  };

  const onEditUserPressed = (userInfo) => {
    setEditUserState({
      isVisible: true,
      isNew: false,
      editorIsAdmin: userItem.userType === 'admin',
      userInfo: {
        userFullname: validateStr(userInfo.userFullname),
        userInstitute: validateStr(userInfo.userInstitute),
        userRole: validateStr(userInfo.userRole),
        userEmail: validateStr(userInfo.userEmail),
        userPhone: validateStr(userInfo.userPhone),

        userUsername: validateStr(userInfo.userUsername),
        userPassword: '',
        userType: validateStr(userInfo.userType),
        userId: validateStr(userInfo.userId),
      },
    });
  };

  //-------------------------------------------------------------------------------

  const calcStatus = (item) => {
    if (item.userBlocked) {
      return 'Blocked';
    }
    if (
      item.userConfigs === null
            || item.userConfigs === undefined
            || item.userConfigs.length === 0
    ) {
      return 'Idle';
    }
    if (item.userConfigs[0].last_send_at && item.userConfigs[0].last_send_at.length > 0) {
      return 'Done';
    }
    return 'In work';
  };

  //-------------------------------------------------------------------------------

  const renderUsersLine = () => state.users.map((item, index) => (
    <MyTableLine
      style={{ width: screenWidth - 100, marginLeft: 50, height: 32 }}
      key={item.userId}
      width={screenWidth - 100}
      arrValues={[
        item.userInstitute, // Bank/Company
        item.userFullname, // Name
        item.userRole, // Role
        item.userPhone, // Phone
        item.userEmail, // Email
        item.userUsername, // Username
        calcStatus(item),
      ]}
      rowIndex={index}
      onCellPressed={(rowIndex, colIndex) => {
      }}
      onActionPressed={(action, rowIndex) => {
        onActionPressed(action, index, false);
      }}
      language={language}
    />
  ));

  //-------------------------------------------------------------------------------

  const renderAdminsLine = () => state.admins.map((item, index) => (
    <MyTableLine
      style={{ width: screenWidth - 100, marginLeft: 50, height: 32 }}
      key={item.userId}
      width={screenWidth - 100}
      arrValues={[
        item.userInstitute, // Bank/Company
        item.userFullname, // Name
        item.userRole, // Role
        item.userPhone, // Phone
        item.userEmail, // Email
        item.userUsername, // Username
        calcStatus(item),
      ]}
      isTheLoggedinUser={item.userId === loggedInUserId}
      canDeleteOrBlock={state.admins.length > 1}
      rowIndex={index}
      onCellPressed={(rowIndex, colIndex) => {
      }}
      onActionPressed={(action, rowIndex) => {
        onActionPressed(action, index, true);
      }}
      language={language}
    />
  ));

  // ######################################################################
  // ###   S e r v e r   s t u f f
  // ######################################################################

  const serverLoadAllUsers = async () => {
    try {
      setAnimating(true);

      getAllUsers(
        token,
        (result) => {
          setAnimating(false);

          // extract admins and/or users:
          let admins = [];
          let users = [];
          result.map((item, index) => {
            // extract 'details' field:
            let userDetails = null;
            if (item.details !== undefined) userDetails = JSON.parse(item.details);

            const newItem = {
              userId: validateStr(item._id),
              userUsername: validateStr(item.userName),
              userPassword: validateStr(item.password),
              userType: validateStr(item.userType),
              userBlocked: validateStr(item.blocked),
              userInstitute: validateStr(item.clientName),
              userRole: validateStr(
                userDetails === null ? '' : userDetails.role,
              ),
              userFullname: validateStr(
                userDetails === null ? '' : userDetails.fullname,
              ),
              userEmail: validateStr(
                userDetails === null ? '' : userDetails.email,
              ),
              userPhone: validateStr(
                userDetails === null ? '' : userDetails.phone,
              ),
              userConfigs: item.configurations,
            };

            if (item.userType === 'admin') {
              admins = [...admins, newItem];
            } else {
              users = [...users, newItem];
            }
          });

          admins.sort((a, b) => {
            if (a.userId === loggedInUserId) return -1;
            if (b.userId === loggedInUserId) return 1;

            let res = a.userInstitute.localeCompare(b.userInstitute);
            if (res === 0) {
              res = a.userFullname.localeCompare(b.userFullname);
            }
            // var res = a.userFullname.localeCompare(b.userFullname);
            return res;
          });

          users.sort((a, b) => {
            let res = a.userInstitute.localeCompare(b.userInstitute);
            if (res === 0) {
              res = a.userFullname.localeCompare(b.userFullname);
            }
            return res;
          });

          setState({ ...state, users, admins });
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const serverCreateUser = async (userInfo) => {
    try {
      setAnimating(true);

      createUser(
        token,
        userInfo,
        (result) => {
          setAnimating(false);
          serverUpdateUserByAdmin(result, userInfo);
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const serverUpdateUserByAdmin = async (userId, userInfo) => {
    try {
      setAnimating(true);

      updateUserByAdmin(
        token,
        userId,
        userInfo,
        (result) => {
          setAnimating(false);
          serverLoadAllUsers();
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const serverDeleteUser = async (userId) => {
    try {
      setAnimating(true);

      deleteUser(
        token,
        userId,
        (result) => {
          setAnimating(false);
          serverLoadAllUsers();
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const serverBlockUser = async (userId) => {
    try {
      setAnimating(true);

      blockUser(
        token,
        userId,
        (result) => {
          setAnimating(false);
          serverLoadAllUsers();
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const serverUnblockUser = async (userId) => {
    try {
      setAnimating(true);

      unblockUser(
        token,
        userId,
        (result) => {
          setAnimating(false);
          serverLoadAllUsers();
        },
        (errMsg) => {
          setAnimating(false);

          console.log(errMsg);
          setMessagePopupState({ isMessageVisible: true, title: errMsg });
        },
      );
    } catch (err) {
    }
  };

  //= =====================================================================

  const gotoMain = (selectedUser) => {
    navigation.navigate('MainScreen', {
      token,
      loggedInUserId,
      loggedInUserItem: userItem,
      userItem: selectedUser,
      onRefreshScreen,
    });
  };

  // ============= render ====================

  if (screenWidth < 900) {
    return (
      <View style={styles.container}>
        <View
          style={{
            height: screenHeight,
            width: screenWidth,
            justifyContent: 'center',
          }}
        >
          <Text style={styles.loadingTitle}>
            {LocStr(Sorry_window_size, language)}
          </Text>
        </View>
      </View>
    );
  }

  if (!fontsLoaded) {
    return <View style={styles.container} />;
  }
  return (
    <View style={styles.container}>
      <View style={styles.bodyStyle}>
        {/* ---------------   U s e r s   T a b l e   ----------------*/}

        <View style={{ width: 1, height: 1, marginTop: 59 }} />

        <ScrollView
          style={{
            width: screenWidth - 100,
            height: screenHeight - 60 - 73 - 20,
            marginTop: 80,
            position: 'absolute',
            flexDirection: 'column',
            // backgroundColor:'#0f0',
          }}
          vertical
          pagingEnabled={false}
          showsVerticalScrollIndicator={false /* IsSafari() */}
          scrollEnabled
        >
          {userItem.userType === 'admin' && state.admins.length > 0 ? (
            <View style={{ width: screenWidth - 100, marginTop: 20 }}>
              <Text
                style={[
                  styles.lblTitle,
                  {
                    marginTop: 0,
                    position: 'absolute',
                  },
                ]}
              >
                {LocStr(PayKey_team, language)}
              </Text>

              <View
                style={{
                  width: screenWidth - 100,
                  marginTop: 28,
                  backgroundColor: '#001a2f',
                }}
              >
                <MyTableHeader
                  style={{
                    width: screenWidth - 100,
                    marginLeft: 50,
                    height: 32,
                  }}
                  width={screenWidth - 100}
                  arrTitles={[
                    'Bank/Company',
                    'Name',
                    'Role',
                    'Phone',
                    'Email',
                    'Username',
                    'Status' /* ,'Password','Type' */,
                  ]}
                  onCellPressed={(index) => {
                  }}
                  language={language}
                />

                {renderAdminsLine()}

                <View style={{ width: 1, height: 1 }} />
              </View>
            </View>
          ) : null}

          {userItem.userType === 'admin' && state.users.length > 0 ? (
            <View style={{ width: screenWidth - 100, marginTop: 20 }}>
              <Text
                style={[
                  styles.lblTitle,
                  {
                    marginTop: 0,
                    position: 'absolute',
                  },
                ]}
              >
                {LocStr(Customers_str, language)}
              </Text>

              <View
                style={{
                  width: screenWidth - 100,
                  marginTop: 28,
                  backgroundColor: '#001a2f',
                }}
              >
                <MyTableHeader
                  style={{
                    width: screenWidth - 100,
                    marginLeft: 50,
                    height: 32,
                  }}
                  width={screenWidth - 100}
                  arrTitles={[
                    'Bank/Company',
                    'Name',
                    'Role',
                    'Phone',
                    'Email',
                    'Username',
                    'Status' /* ,'Password','Type' */,
                  ]}
                  onCellPressed={(index) => {
                  }}
                  language={language}
                />

                {renderUsersLine()}

                <View style={{ width: 1, height: 1 }} />
              </View>
            </View>
          ) : null}
        </ScrollView>

        {editUserState.isVisible ? (
          <MyEditUserPopup
            onValueChanged={(userInfo, isNew) => {
              onUserChanged(userInfo, isNew);
            }}
            onCanceled={() => onUserEditCanceled()}
            userInfo={editUserState.userInfo}
            isNew={editUserState.isNew}
            editorIsAdmin={editUserState.editorIsAdmin}
            language={language}
          />
        ) : null}
      </View>

      {/* =============================   B o t t o m   b a r   ============================= */}

      <View
        style={{
          height: 36,
          width: screenWidth - 100,
          marginLeft: 50,
          marginBottom: 20,
        }}
      >
        <TouchableOpacity
          style={{
            height: 36,
            width: 200,
            marginLeft: screenWidth - 200 - 100,
            marginTop: 0,
            position: 'absolute',
            borderRadius: 7,
            justifyContent: 'center',
            backgroundColor: '#008cff',
          }}
          onPress={() => {
            onAddUserPressed();
          }}
        >
          <Text style={styles.bottomLabelWhite}>
            {LocStr(Add_user, language)}
          </Text>
        </TouchableOpacity>
      </View>

      {/* ================================   T o p   b a r   ================================ */}

      <View
        style={{
          height: 60,
          width: screenWidth,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 12,
          justifyContent: 'center',
          backgroundColor: '#fff',
          position: 'absolute',
        }}
      >
        <View style={styles.topBarImageAndVer}>
          <Image
            fadeDuration={0}
            style={styles.topBarImage}
            source={img_paykey}
          />
        </View>
        {animating ? (
          <ActivityIndicator
            animating={animating}
            color="#001a2f"
            size="large"
            style={styles.activityIndicator}
          />
        ) : (
          <TouchableOpacity
            style={{
              width: 36,
              height: 36,
              marginLeft: screenWidth - 20 - 36,
              position: 'absolute',
              borderRadius: 18,
              backgroundColor: '#718096',
              justifyContent: 'center',
            }}
            activeOpacity={1}
            onPress={() => {
              setMainMenuState({ isMenuOpen: !mainMenuState.isMenuOpen });
            }}
          >
            <Text
              style={{
                width: 36,
                textAlign: 'center',
                fontFamily: 'Quicksand-SemiBold',
                fontSize: 16,
                color: '#fff',
              }}
            >
              {userItem === null ? '' : calcInitials(userItem.userFullname)}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {mainMenuState.isMenuOpen ? (
        <TouchableOpacity
          style={{
            width: screenWidth,
            height: screenHeight,
            marginTop: 0,
            position: 'absolute',
            backgroundColor: 'rgba(0,0,0,0.0)',
          }}
          activeOpacity={1}
          onPress={() => {
            setMainMenuState({ isMenuOpen: false });
          }}
        >
          <View
            style={{
              width: 314,
              height: 92 + 46,
              marginLeft: screenWidth - 314,
              marginTop: 60,
              position: 'absolute',
              backgroundColor: '#fff',
              borderColor: '#cbd5e0',
              borderRadius: 4,
              borderWidth: 1,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.22,
              shadowRadius: 12,
            }}
          >
            <View
              style={{ width: 314, height: 92, backgroundColor: '#edf2f7' }}
            >
              <Text
                style={{
                  marginLeft: 30,
                  marginTop: 22,
                  width: 254,
                  fontFamily: 'Rubik-Regular',
                  fontSize: 16,
                  textAlign: 'left',
                  color: '#001a2f',
                }}
              >
                {userItem.userFullname}
              </Text>
              <Text
                style={{
                  marginLeft: 30,
                  marginTop: 8,
                  width: 254,
                  fontFamily: 'Rubik-Regular',
                  fontSize: 16,
                  textAlign: 'left',
                  color: '#001a2f',
                }}
              >
                {userItem.userEmail}
              </Text>
            </View>
            <TouchableOpacity
              style={{
                width: 314,
                height: 46,
                marginTop: 0,
                justifyContent: 'center',
              }}
              onPress={() => {
                setMainMenuState({ isMenuOpen: false });
                onLogoutPressed();
              }}
            >
              <Text
                style={{
                  marginLeft: 30,
                  width: 254,
                  fontFamily: 'Rubik-Regular',
                  fontSize: 16,
                  textAlign: 'left',
                  color: '#001a2f',
                }}
              >
                {LocStr(log_out, language)}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      ) : null}

      {messagePopupState.isMessageVisible ? (
        <MyMessagePopup
          parentWidth={screenWidth}
          onOkPressed={() => setMessagePopupState({ isMessageVisible: false, title: '' })}
          okTitle={LocStr(ok_str, language)}
          message={messagePopupState.title}
          language={language}
        />
      ) : null}

      {questionPopupState.isQuestionVisible ? (
        <MyAskQuestionPopup
          parentWidth={screenWidth}
          onYesPressed={(questionId, userId) => {
            onQuestionAnswerWasYes(questionId, userId);
          }}
          onNoPressed={(questionId, userId) => {
            onQuestionAnswerWasNo(questionId, userId);
          }}
          questionId={questionPopupState.questionId}
          auxString={questionPopupState.userId}
          yesTitle={LocStr(yes_str, language)}
          noTitle={LocStr(no_str, language)}
          question={questionPopupState.title}
          language={language}
        />
      ) : null}

      <Text
        style={{
          marginLeft: screenWidth - 100 - 4,
          marginTop: screenHeight - 15,
          width: 100,
          height: 11,
          position: 'absolute',
          textAlign: 'right',
          fontFamily: 'Rubik-Medium',
          fontSize: 10,
          color: '#008cff',
        }}
      >
        {expo.version}
      </Text>
    </View>
  );
}

// ============= styles ====================

AdminScreen.navigationOptions = {
  headerShown: false,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#f1f5f9',
    // prevent user-select on texts
    userSelect: 'none',
  },

  loadingTitle: {
    textAlign: 'center',
    fontFamily: 'Quicksand-Medium',
    fontSize: 20,
    color: '#001a2f',
    // alignSelf: 'center',
    // backgroundColor: '#f00',
  },

  topBarTitle: {
    height: 25,
    textAlign: 'center',
    fontFamily: 'Quicksand-Medium',
    fontSize: 20,
    color: '#001a2f',
    backgroundColor: '#fff',
  },

  bodyStyle: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    // justifyContent:'center',
    // flexDirection: 'flex-end',
    // backgroundColor: '#f00',
  },
  lblTitle: {
    width: 290,
    marginTop: 2,
    fontFamily: 'Rubik-Regular',
    fontSize: 18,
    color: '#001a2f',
    // position: 'absolute',
  },
  btnAddUser: {
    height: 36,
    width: 200,
    marginLeft: 200,
    marginTop: 20,
    borderRadius: 7,
    justifyContent: 'center',
    backgroundColor: '#008cff',
  },
  bottomLabelWhite: {
    // height: 70,
    fontFamily: 'Rubik-Medium',
    fontSize: 18,
    color: '#fff',
    position: 'absolute',
    // marginTop: 52,
    alignSelf: 'center',
    // backgroundColor: '#f00',
  },
  activityIndicator: {
    position: 'absolute',
    alignSelf: 'flex-end',
    marginRight: 15,
    height: 30,
    marginTop: 0,
  },
  topBarImageAndVer: {
    marginLeft: 24,
    marginTop: 0,
    width: 136 + 30,
    height: 60,
    position: 'absolute',
  },
  topBarImage: {
    marginLeft: 0,
    marginTop: 12,
    width: 136,
    height: 36,
    resizeMode: 'contain',
    position: 'absolute',
  },
});
